"use client"

import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  Search,
  Plus,
  Loader,
  AlertCircle,
  Pencil,
  Trash2,
  X,
  Upload,
  User,
  BookOpen,
  Bookmark,
  Clock,
  Calendar,
  ChevronRight,
  FileText,
  CheckCircle,
  MoreHorizontal,
  Filter,
  ChevronDown,
  Grid3X3,
  List,
} from "lucide-react"
import { getAllbooks } from "./api"
import { generatePresignedUrl, postTeacherLibraryFile } from "../postFileAws"
import { postAws } from "../postAws"
import { Worker } from "@react-pdf-viewer/core"
import { Viewer } from "@react-pdf-viewer/core"
import "@react-pdf-viewer/core/lib/styles/index.css"
import "@react-pdf-viewer/default-layout/lib/styles/index.css"

const Books = () => {
  const subjectSelected = localStorage.getItem("subject")
  const [books, setBooks] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [newBook, setNewBook] = useState({ title: "", author: "", subject: subjectSelected, file: null })
  const [isUploading, setIsUploading] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingBook, setEditingBook] = useState(null)
  const [editError, setEditError] = useState(null)
  const [booksFetched, setBooksFetched] = useState(false)
  const [isDeletingBook, setIsDeletingBook] = useState(false)
  const [pdfPreview, setPdfPreview] = useState(null)
  const [uploadError, setUploadError] = useState(null)
  const [activeBookMenu, setActiveBookMenu] = useState(null)
  const [flashMessage, setFlashMessage] = useState(null)
  const [filterSubject, setFilterSubject] = useState("all")
  const [sortOption, setSortOption] = useState("newest")
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [viewMode, setViewMode] = useState("card") // New state for view toggle
  const organisationId = localStorage.getItem("organisationId")
  const navigate = useNavigate()
  const teacherId = localStorage.getItem("teacherId")
  const classRoomId = localStorage.getItem("classRoomId")

  const fetchBooks = useCallback(async () => {
    if (booksFetched) {
      setIsLoading(false)
      return
    }
    try {
      setIsLoading(true)
      if (!booksFetched) {
        const response = await getAllbooks(classRoomId)
        if (response.body.data) {
          const booksWithPresignedUrls = await Promise.all(
            response.body.data.map(async (book) => {
              const presignedUrl = await generatePresignedUrl(book.fileUrl)
              return { ...book, presignedUrl }
            }),
          )
          setBooks(booksWithPresignedUrls)
          setBooksFetched(true)
        } else {
          setBooks([])
          setBooksFetched(true)
        }
      }
    } catch (error) {
      console.error("Error fetching books:", error)
      setError("Failed to load books. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }, [booksFetched, classRoomId])

  useEffect(() => {
    fetchBooks()
  }, [fetchBooks])

  useEffect(() => {
    if (flashMessage) {
      const timer = setTimeout(() => {
        setFlashMessage(null)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [flashMessage])

  const handleInputChange = (e) => {
    const { name, value, files } = e.target
    if (name === "file") {
      const file = files[0]
      setNewBook({ ...newBook, file })
      if (file && file.type === "application/pdf") {
        const reader = new FileReader()
        reader.onload = (e) => setPdfPreview(e.target.result)
        reader.readAsDataURL(file)
      } else {
        setPdfPreview(null)
      }
    } else {
      setNewBook({ ...newBook, [name]: value })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsUploading(true)
    setUploadError(null)

    const existingBook = books.find((book) => book.fileTitle.toLowerCase() === newBook.title.toLowerCase())
    if (existingBook) {
      setUploadError("A book with this title already exists.")
      setIsUploading(false)
      return
    }

    try {
      const uploadToS3 = await postTeacherLibraryFile(newBook.file, teacherId, classRoomId, newBook.title)
      console.log(uploadToS3)

      const uploadData = {
        body: {
          fileTitle: newBook.title,
          authorName: newBook.author,
          fileSubject: newBook.subject,
          fileUrl: uploadToS3.url,
          fileUri: uploadToS3.uri,
          organisationId: organisationId,
          classRoomId: classRoomId,
        },
        request: "upload-Book",
      }

      const response = await postAws("https://api-dev.aurait.ai/library", "POST", uploadData)

      if (response.statusCode === 202) {
        // Handle the new scheduled upload success
        setFlashMessage({
          type: "success",
          message: "Book uploadation initiated! It will be available for you soon :)",
        })
        setIsUploadModalOpen(false)
        setNewBook({ title: "", author: "", subject: subjectSelected, file: null })
        setPdfPreview(null) // Clear PDF preview
        // Do NOT fetch books immediately, as it's scheduled in the background
      } else if (response.statusCode === 201) {
        // Handle immediate upload success (if still possible)
        setBooksFetched(false)
        await fetchBooks()
        setIsUploadModalOpen(false)
        setNewBook({ title: "", author: "", subject: subjectSelected, file: null })
        setPdfPreview(null) // Clear PDF preview
        setFlashMessage({ type: "success", message: "Book uploaded successfully!" })
      } else {
        throw new Error("Failed to upload book")
      }
    } catch (error) {
      console.error("Error uploading book:", error)
      setUploadError("Failed to upload book. Please try again.")
    } finally {
      setIsUploading(false)
    }
  }

  const handleEditBook = async (e) => {
    e.preventDefault()
    setIsUploading(true)
    setEditError(null)

    const existingBook = books.find(
      (book) => book.fileTitle.toLowerCase() === editingBook.fileTitle.toLowerCase() && book._id !== editingBook._id,
    )
    if (existingBook) {
      setEditError("A book with this title already exists.")
      setIsUploading(false)
      return
    }

    const updateData = {
      body: {
        bookId: editingBook._id,
        updatedFields: {
          fileTitle: editingBook.fileTitle,
          authorName: editingBook.authorName,
          fileSubject: editingBook.fileSubject,
        },
      },
      request: "update-book",
    }
    try {
      const response = await postAws("https://api-dev.aurait.ai/library", "POST", updateData)
      if (response.statusCode === 200) {
        setBooksFetched(false)
        await fetchBooks()
        setIsEditModalOpen(false)
        setEditingBook(null)
        setFlashMessage({ type: "success", message: "Book updated successfully!" })
      } else {
        throw new Error(response.body ? JSON.parse(response.body).message : "Failed to update book")
      }
    } catch (error) {
      console.error("Error updating book:", error)
      setEditError(error.message || "Failed to update book. Please try again.")
    } finally {
      setIsUploading(false)
    }
  }

  const handleAssignmentRedirect = (book) => {
    localStorage.setItem("selectedBookUri", book.fileUri)
    navigate("/assignments/description")
    setActiveBookMenu(null)
  }

  const handleDeleteBook = async (book) => {
    if (window.confirm(`Are you sure you want to delete "${book.fileTitle}"?`)) {
      setIsDeletingBook(true)
      const deleteBookData = {
        body: {
          bookId: book._id,
        },
        request: "delete-book",
      }
      try {
        const deleteBook = await postAws("https://api-dev.aurait.ai/library", "POST", deleteBookData)
        console.log(deleteBook)
        if (deleteBook.statusCode === 200) {
          setBooksFetched(false)
          await fetchBooks()
          setFlashMessage({ type: "success", message: "Book deleted successfully!" })
        } else {
          console.error("Failed to delete book:", deleteBook)
          setError("Failed to delete book. Please try again.")
        }
      } catch (error) {
        console.error("Error deleting book:", error)
        setError("An error occurred while deleting the book. Please try again.")
      } finally {
        setIsDeletingBook(false)
        setActiveBookMenu(null)
      }
    }
  }

  const handleBookClick = (book) => {
    navigate(`/books/studybuddy/${book._id}`)
  }

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" }
    return new Date(dateString).toLocaleDateString(undefined, options)
  }

  const formatTime = (dateString) => {
    const options = { hour: "2-digit", minute: "2-digit" }
    return new Date(dateString).toLocaleTimeString(undefined, options)
  }

  const getUniqueSubjects = () => {
    const subjects = books.map((book) => book.fileSubject)
    return ["all", ...new Set(subjects)]
  }

  const filteredBooks = books
    .filter((book) => {
      const matchesSearch =
        book.fileTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        book.authorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        book.fileSubject.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesSubject = filterSubject === "all" || book.fileSubject === filterSubject
      return matchesSearch && matchesSubject
    })
    .sort((a, b) => {
      if (sortOption === "newest") {
        return new Date(b.createdAt) - new Date(a.createdAt)
      } else if (sortOption === "oldest") {
        return new Date(a.createdAt) - new Date(b.createdAt)
      } else if (sortOption === "title") {
        return a.fileTitle.localeCompare(b.fileTitle)
      } else if (sortOption === "author") {
        return a.authorName.localeCompare(b.authorName)
      }
      return 0
    })

  const getFilterLabel = () => {
    if (filterSubject !== "all") {
      return filterSubject.length > 8 ? filterSubject.substring(0, 8) + "..." : filterSubject
    }
    return "All Subjects"
  }

  // Open upload modal function with state reset
  const openUploadModal = () => {
    setIsUploadModalOpen(true)
    setPdfPreview(null) // Clear any existing PDF preview
    setUploadError(null)
    setNewBook({ title: "", author: "", subject: subjectSelected, file: null })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md text-center max-w-md mx-auto">
          <Loader className="animate-spin text-blue-600 mx-auto mb-3 sm:mb-4" size={36} />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Loading your library</h2>
          <p className="text-sm sm:text-base text-gray-600">Please wait while we fetch your books...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md text-center max-w-md mx-auto">
          <AlertCircle className="text-red-500 mx-auto mb-3 sm:mb-4" size={36} />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Something went wrong</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">{error}</p>
          <button
            onClick={() => {
              setBooksFetched(false)
              setError(null)
              fetchBooks()
            }}
            className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
      {/* Flash Message */}
      {flashMessage && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-xl max-w-md animate-fade-in flex items-center ${
            flashMessage.type === "success" ? "bg-green-500" : "bg-red-500"
          } text-white`}
        >
          {flashMessage.type === "success" ? (
            <CheckCircle className="mr-3 flex-shrink-0" size={20} />
          ) : (
            <AlertCircle className="mr-3 flex-shrink-0" size={20} />
          )}
          <p>{flashMessage.message}</p>
        </div>
      )}

      <div className="container mx-auto px-2 md:px-4 py-0 max-w-7xl">
        {/* Header Section with Search, Filter, View Toggle, and Upload Button */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 md:mt-8">
          <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-3 md:px-4 py-3 md:py-4">
            {/* Mobile Layout */}
            <div className="block lg:hidden">
              <div className="flex items-center gap-2">
                {/* View Toggle - moved to left */}
                <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                  <button
                    onClick={() => setViewMode("card")}
                    className={`p-1.5 rounded-l-lg transition-colors ${
                      viewMode === "card" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                    }`}
                  >
                    <Grid3X3 size={12} />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-1.5 rounded-r-lg transition-colors ${
                      viewMode === "list" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                    }`}
                  >
                    <List size={12} />
                  </button>
                </div>
                {/* Search */}
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-1.5 pl-7 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-xs"
                  />
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white/70" size={12} />
                </div>
                {/* Filter */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm text-white text-xs font-medium border border-white/30"
                  >
                    <Filter size={12} />
                    <ChevronDown
                      size={10}
                      className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  </button>
                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                      {getUniqueSubjects().map((subject) => (
                        <button
                          key={subject}
                          onClick={() => {
                            setFilterSubject(subject)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full px-2 py-1.5 text-xs text-left transition-colors hover:bg-gray-50 ${
                            filterSubject === subject ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                          }`}
                        >
                          {subject === "all" ? "All Subjects" : subject}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Upload Button */}
                <button
                  onClick={openUploadModal}
                  className="bg-white/20 backdrop-blur-sm text-white px-2 py-1.5 rounded-lg hover:bg-white/30 transition-colors duration-300 flex items-center gap-1 border border-white/30 text-xs font-medium"
                >
                  <Plus size={12} />
                  <span>Upload</span>
                </button>
              </div>
            </div>
            {/* Desktop Layout */}
            <div className="hidden lg:flex lg:items-center justify-between gap-4">
              {/* View Toggle - moved to left side */}
              <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                <button
                  onClick={() => setViewMode("card")}
                  className={`flex items-center gap-1 p-2 rounded-l-lg transition-colors text-xs ${
                    viewMode === "card" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                  }`}
                  title="Card View"
                >
                  <Grid3X3 size={14} />
                  <span>Card</span>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`flex items-center gap-1 p-2 rounded-r-lg transition-colors text-xs ${
                    viewMode === "list" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                  }`}
                  title="List View"
                >
                  <List size={14} />
                  <span>List</span>
                </button>
              </div>
              {/* Search, Filter, and Upload - Right side */}
              <div className="flex items-center gap-4">
                {/* Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search books"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-64 px-4 py-2 pl-10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-sm"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" size={14} />
                </div>
                {/* Filter */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 backdrop-blur-sm text-white text-sm font-medium border border-white/30 hover:bg-white/30 transition-colors"
                  >
                    <Filter size={14} />
                    <span>{getFilterLabel()}</span>
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  </button>
                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                      {getUniqueSubjects().map((subject) => (
                        <button
                          key={subject}
                          onClick={() => {
                            setFilterSubject(subject)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full px-3 py-2 text-sm text-left transition-colors hover:bg-gray-50 ${
                            filterSubject === subject ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                          }`}
                        >
                          {subject === "all" ? "All Subjects" : subject}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Upload Button */}
                <button
                  onClick={openUploadModal}
                  className="bg-white/20 backdrop-blur-sm text-white px-5 py-2 rounded-lg hover:bg-white/30 transition-colors duration-300 flex items-center gap-2 border border-white/30 text-sm font-medium"
                >
                  <Plus size={16} />
                  <span>Upload Book</span>
                </button>
              </div>
            </div>
          </div>
          {/* Content Section */}
          <div className="p-3 md:p-6">
            {/* Books Display */}
            {books.length === 0 ? (
              <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 text-indigo-600 mb-4">
                  <BookOpen className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Your library is empty</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-6">
                  Upload your first book to start creating assignments and using the Study Buddy feature.
                </p>
                <button
                  onClick={openUploadModal}
                  className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors duration-300 flex items-center gap-2 mx-auto"
                >
                  <Upload size={20} />
                  <span>Upload Your First Book</span>
                </button>
              </div>
            ) : filteredBooks.length === 0 ? (
              <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 text-amber-600 mb-4">
                  <Search className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No matching books found</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-6">
                  Try adjusting your search terms or filters to find what you're looking for.
                </p>
                <button
                  onClick={() => {
                    setSearchTerm("")
                    setFilterSubject("all")
                  }}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300"
                >
                  Clear Filters
                </button>
              </div>
            ) : viewMode === "card" ? (
              // Card View
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {filteredBooks.map((book) => (
                  <div
                    key={book._id}
                    className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl flex flex-col relative group max-w-xs mx-auto w-full"
                  >
                    {/* Book Header with Title */}
                    <div className="bg-gradient-to-r from-indigo-600 to-blue-600 h-16 flex items-center justify-between px-4 relative">
                      <h3 className="text-white font-semibold line-clamp-1 text-sm">{book.fileTitle}</h3>
                      {/* Menu Button */}
                      <div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setActiveBookMenu(activeBookMenu === book._id ? null : book._id)
                          }}
                          className="p-1.5 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors"
                          aria-label="Book options"
                        >
                          <MoreHorizontal size={16} />
                        </button>
                        {/* Dropdown Menu */}
                        {activeBookMenu === book._id && (
                          <div
                            className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl z-10 border border-gray-100 py-1 animate-scale-in"
                            style={{ maxWidth: "calc(100vw - 2rem)", right: "0", left: "auto" }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setEditingBook(book)
                                setIsEditModalOpen(true)
                                setActiveBookMenu(null)
                              }}
                              className="w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                            >
                              <Pencil size={16} className="mr-2 text-indigo-500" />
                              Edit Book Details
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleAssignmentRedirect(book)
                              }}
                              className="w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                            >
                              <FileText size={16} className="mr-2 text-green-500" />
                              Create Assignment
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteBook(book)
                              }}
                              className="w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center transition-colors"
                            >
                              <Trash2 size={16} className="mr-2" />
                              Delete Book
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Book Content */}
                    <div className="p-4 flex-grow flex flex-col">
                      <div className="mb-3">
                        <div className="flex items-center text-gray-600">
                          <User size={14} className="mr-1.5 text-gray-400" />
                          <p className="line-clamp-1 text-sm">{book.authorName}</p>
                        </div>
                      </div>
                      <div className="bg-indigo-50 px-3 py-1.5 rounded-full text-indigo-700 text-xs font-medium inline-flex items-center self-start mb-3">
                        <Bookmark size={12} className="mr-1" />
                        {book.fileSubject}
                      </div>
                      <div className="mt-auto space-y-1.5 pt-2 border-t border-gray-100">
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span className="flex items-center">
                            <Calendar size={12} className="mr-1.5" />
                            Added:
                          </span>
                          <span>{formatDate(book.createdAt)}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span className="flex items-center">
                            <Clock size={12} className="mr-1.5" />
                            Updated:
                          </span>
                          <span>{formatDate(book.updatedAt)}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleBookClick(book)}
                        className="mt-3 bg-indigo-600 text-white px-3 py-2 rounded-lg hover:bg-indigo-700 transition-all duration-300 shadow-md flex items-center justify-center gap-2 w-full text-sm"
                      >
                        <BookOpen size={16} />
                        <span>Open Study Buddy</span>
                        <ChevronRight size={14} className="ml-auto" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // List View - Table Format
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Book Name
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Subject
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Author
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Added
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Updated
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredBooks.map((book) => (
                        <tr key={book._id} className="hover:bg-gray-50 transition-colors duration-200">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                                <BookOpen className="w-4 h-4 text-white" />
                              </div>
                              <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                                {book.fileTitle}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {book.fileSubject}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <User size={14} className="mr-2 text-gray-400" />
                              <span className="max-w-32 truncate">{book.authorName}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <Calendar size={14} className="mr-2 text-gray-400" />
                              {formatDate(book.createdAt)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <Clock size={14} className="mr-2 text-gray-400" />
                              {formatDate(book.updatedAt)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleBookClick(book)}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                              >
                                <BookOpen size={14} className="mr-1" />
                                Study Buddy
                              </button>
                              {/* Menu Button */}
                              <div className="relative">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setActiveBookMenu(activeBookMenu === book._id ? null : book._id)
                                  }}
                                  className="inline-flex items-center p-1.5 border border-gray-300 rounded-md text-gray-400 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                                  aria-label="Book options"
                                >
                                  <MoreHorizontal size={16} />
                                </button>
                                {/* Dropdown Menu */}
                                {activeBookMenu === book._id && (
                                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl z-10 border border-gray-100 py-1 animate-scale-in">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setEditingBook(book)
                                        setIsEditModalOpen(true)
                                        setActiveBookMenu(null)
                                      }}
                                      className="w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                                    >
                                      <Pencil size={16} className="mr-2 text-indigo-500" />
                                      Edit Book Details
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleAssignmentRedirect(book)
                                      }}
                                      className="w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                                    >
                                      <FileText size={16} className="mr-2 text-green-500" />
                                      Create Assignment
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleDeleteBook(book)
                                      }}
                                      className="w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center transition-colors"
                                    >
                                      <Trash2 size={16} className="mr-2" />
                                      Delete Book
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4 rounded-t-xl">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white flex items-center">
                  <Upload className="mr-2" size={24} />
                  Upload New Book
                </h2>
                <button
                  onClick={() => {
                    setIsUploadModalOpen(false)
                    setPdfPreview(null)
                    setUploadError(null)
                    setNewBook({ title: "", author: "", subject: subjectSelected, file: null })
                  }}
                  className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/20 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className="p-6">
              {uploadError && (
                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-lg flex items-start">
                  <AlertCircle className="mr-3 mt-0.5 flex-shrink-0" size={20} />
                  <p>{uploadError}</p>
                </div>
              )}
              <form onSubmit={handleSubmit} className="flex flex-col lg:flex-row gap-8">
                <div className="flex-1 space-y-5">
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                      Book Title
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="title"
                        name="title"
                        value={newBook.title}
                        onChange={handleInputChange}
                        className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        required
                      />
                      <BookOpen
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        size={18}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="author" className="block text-sm font-medium text-gray-700 mb-1">
                      Author
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="author"
                        name="author"
                        value={newBook.author}
                        onChange={handleInputChange}
                        className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        required
                      />
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                      Subject
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={newBook.subject}
                        onChange={handleInputChange}
                        className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        required
                      />
                      <Bookmark
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        size={18}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="file" className="block text-sm font-medium text-gray-700 mb-1">
                      PDF File
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        id="file"
                        name="file"
                        onChange={handleInputChange}
                        className="hidden"
                        accept=".pdf"
                        required
                      />
                      <label
                        htmlFor="file"
                        className="flex items-center justify-center w-full px-4 py-3 border border-gray-300 rounded-lg cursor-pointer bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <Upload className="mr-2 text-gray-400" size={18} />
                        <span className="text-gray-600 truncate">
                          {newBook.file ? newBook.file.name : "Choose a PDF file"}
                        </span>
                      </label>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Only PDF files are supported</p>
                  </div>
                  <div className="flex justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setIsUploadModalOpen(false)
                        setPdfPreview(null)
                        setUploadError(null)
                        setNewBook({ title: "", author: "", subject: subjectSelected, file: null })
                      }}
                      className="px-5 py-2.5 mr-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`px-6 py-2.5 rounded-lg transition-colors duration-300 flex items-center justify-center shadow-md ${
                        !newBook.file || isUploading
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-indigo-600 text-white hover:bg-indigo-700"
                      }`}
                      disabled={!newBook.file || isUploading}
                    >
                      {isUploading ? (
                        <>
                          <Loader className="animate-spin mr-2" size={20} />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2" size={20} />
                          Upload Book
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex-1 mt-6 lg:mt-0">
                  <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 h-[300px] lg:h-[500px]">
                    {pdfPreview ? (
                      <div className="h-full">
                        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                          <Viewer fileUrl={pdfPreview} />
                        </Worker>
                      </div>
                    ) : (
                      <div className="h-full flex items-center justify-center text-gray-500">
                        <div className="text-center p-6">
                          <FileText size={64} className="mx-auto mb-4 text-gray-300" />
                          <p className="text-lg font-medium text-gray-600 mb-2">PDF Preview</p>
                          <p className="text-sm text-gray-500 max-w-xs">
                            Upload a PDF file to see a preview here. This helps ensure you've selected the correct
                            document.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-scale-in overflow-y-auto max-h-[90vh]">
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4 rounded-t-xl">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white flex items-center">
                  <Pencil className="mr-2" size={20} />
                  Edit Book Details
                </h2>
                <button
                  onClick={() => {
                    setIsEditModalOpen(false)
                    setEditingBook(null)
                    setEditError(null)
                  }}
                  className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/20 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6">
              {editError && (
                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-lg flex items-start">
                  <AlertCircle className="mr-3 mt-0.5 flex-shrink-0" size={20} />
                  <p>{editError}</p>
                </div>
              )}
              <form onSubmit={handleEditBook} className="space-y-5">
                <div>
                  <label htmlFor="edit-title" className="block text-sm font-medium text-gray-700 mb-1">
                    Book Title
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="edit-title"
                      value={editingBook?.fileTitle || ""}
                      onChange={(e) => setEditingBook({ ...editingBook, fileTitle: e.target.value })}
                      className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      required
                    />
                    <BookOpen className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  </div>
                </div>
                <div>
                  <label htmlFor="edit-author" className="block text-sm font-medium text-gray-700 mb-1">
                    Author
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="edit-author"
                      value={editingBook?.authorName || ""}
                      onChange={(e) => setEditingBook({ ...editingBook, authorName: e.target.value })}
                      className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      required
                    />
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  </div>
                </div>
                <div>
                  <label htmlFor="edit-subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="edit-subject"
                      value={editingBook?.fileSubject || ""}
                      onChange={(e) => setEditingBook({ ...editingBook, fileSubject: e.target.value })}
                      className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      required
                    />
                    <Bookmark className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  </div>
                </div>
                <div className="flex justify-end pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false)
                      setEditingBook(null)
                      setEditError(null)
                    }}
                    className="px-5 py-2.5 mr-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300 flex items-center justify-center shadow-md"
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <>
                        <Loader className="animate-spin mr-2" size={20} />
                        Updating...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2" size={20} />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Books
